import React, { useEffect, useState } from "react";
import { Download, Share } from "lucide-react";

/**
 * Subtle "install / add to home screen" link that adapts to the platform:
 *
 *  • Android / desktop Chrome-Edge — captures the browser's beforeinstallprompt
 *    event and triggers the NATIVE install dialog on click (true one-tap).
 *  • iOS Safari — no install API exists, so we show a tiny instructions popover
 *    (Share → Add to Home Screen).
 *  • Already installed (display-mode: standalone) OR unsupported browsers —
 *    renders nothing.
 *
 * Self-contained; drop it anywhere. `className` lets the host style the text.
 */
export default function InstallAppLink({ className = "" }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null); // Android/desktop
  const [showIosHelp, setShowIosHelp] = useState(false);

  const isStandalone =
    (typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(display-mode: standalone)").matches) ||
    (typeof navigator !== "undefined" && navigator.standalone === true);

  const isIos =
    typeof navigator !== "undefined" &&
    /iPad|iPhone|iPod/.test(navigator.userAgent || "") &&
    !window.MSStream;

  useEffect(() => {
    const onPrompt = (e) => {
      e.preventDefault();          // stop Chrome's mini-infobar
      setDeferredPrompt(e);        // stash it for our button
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    // Once installed, clear the prompt so the link disappears
    const onInstalled = () => setDeferredPrompt(null);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // Already installed → nothing to offer.
  if (isStandalone) return null;
  // Show only when we can actually do something: a captured prompt (Android/
  // desktop) or iOS (where we can at least give instructions).
  if (!deferredPrompt && !isIos) return null;

  const onClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      try { await deferredPrompt.userChoice; } catch {}
      setDeferredPrompt(null);     // can only be used once
      return;
    }
    if (isIos) setShowIosHelp((v) => !v);
  };

  return (
    <span className="relative inline-flex">
      <button
        type="button"
        onClick={onClick}
        className={`inline-flex items-center gap-1.5 transition-opacity hover:opacity-80 ${className}`}
        data-testid="install-app-link"
      >
        {isIos && !deferredPrompt ? <Share className="w-3.5 h-3.5" /> : <Download className="w-3.5 h-3.5" />}
        {isIos && !deferredPrompt ? "Add to home screen" : "Install app"}
      </button>

      {showIosHelp && (
        <span
          className="absolute bottom-full mb-2 left-0 z-[120] w-60 rounded-xl px-3 py-2.5 text-left rl-frost border-[hsl(var(--border))]"
          role="status"
        >
          <span className="block text-xs text-[hsl(var(--foreground))] leading-snug">
            In Safari, tap the <Share className="inline w-3.5 h-3.5 align-text-bottom" /> Share button,
            then choose <span className="font-semibold">Add to Home Screen</span>.
          </span>
        </span>
      )}
    </span>
  );
}
